import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  animate,
  AnimatePresence,
  motion,
  useMotionValue,
} from "motion/react";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import styles from "./ImageLightbox.module.css";

export interface LightboxImage {
  src: string;
  alt: string;
}

interface ImageLightboxProps {
  open: boolean;
  images: LightboxImage[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onClose: () => void;
}

export function ImageLightbox({
  open,
  images,
  activeIndex,
  onActiveIndexChange,
  onClose,
}: ImageLightboxProps) {
  const reduceMotion = useReducedMotion();
  const viewportRef =
    useRef<HTMLDivElement>(null);
  const trackRef =
    useRef<HTMLDivElement>(null);
  const widthRef = useRef(0);
  const activeIndexRef =
    useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const animationRef = useRef<
    ReturnType<typeof animate> | null
  >(null);
  const wheelDeltaRef = useRef(0);
  const wheelLockedRef = useRef(false);
  const wheelTimerRef = useRef<
    number | null
  >(null);
  const interactionReadyAtRef = useRef(0);
  const lightboxHistoryActiveRef =
    useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const trackX = useMotionValue(0);
  const [viewportWidth, setViewportWidth] =
    useState(0);
  const [slideStep, setSlideStep] =
    useState(0);

  const moveTo = useCallback(
    (index: number) => {
      const nextIndex = Math.min(
        Math.max(index, 0),
        Math.max(images.length - 1, 0),
      );
      const destination =
        -nextIndex * widthRef.current;

      animationRef.current?.stop();
      onActiveIndexChange(nextIndex);

      if (reduceMotion) {
        trackX.set(destination);
        return;
      }

      animationRef.current = animate(
        trackX,
        destination,
        {
          type: "spring",
          stiffness: 270,
          damping: 34,
          mass: 0.9,
        },
      );
    },
    [
      images.length,
      onActiveIndexChange,
      reduceMotion,
      trackX,
    ],
  );
  const moveToRef = useRef(moveTo);
  moveToRef.current = moveTo;

  const requestClose = useCallback(() => {
    if (lightboxHistoryActiveRef.current) {
      window.history.back();
      return;
    }

    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!open) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    const updateWidth = () => {
      const width =
        viewport.getBoundingClientRect()
          .width;
      const gap = trackRef.current
        ? Number.parseFloat(
            window.getComputedStyle(
              trackRef.current,
            ).columnGap,
          ) || 0
        : 0;
      const step = width + gap;
      widthRef.current = step;
      setViewportWidth(width);
      setSlideStep(step);
      trackX.set(
        -activeIndexRef.current * step,
      );
    };

    updateWidth();
    const observer =
      new ResizeObserver(updateWidth);
    observer.observe(viewport);

    return () => {
      observer.disconnect();
      animationRef.current?.stop();
    };
  }, [open, trackX]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow =
      document.documentElement.style
        .overflow;
    document.documentElement.style
      .overflow = "hidden";
    document.documentElement.dataset
      .imageLightboxOpen = "true";
    interactionReadyAtRef.current =
      window.performance.now() + 720;

    if (!lightboxHistoryActiveRef.current) {
      lightboxHistoryActiveRef.current =
        true;
      window.history.pushState(
        {
          ...window.history.state,
          portfolioImageLightbox: true,
        },
        "",
        window.location.href,
      );
    }

    const handlePopState = () => {
      if (
        !lightboxHistoryActiveRef.current
      ) {
        return;
      }

      lightboxHistoryActiveRef.current =
        false;
      onCloseRef.current();
    };

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (event.key === "Escape") {
        requestClose();
      } else if (event.key === "ArrowLeft") {
        moveToRef.current(
          activeIndexRef.current - 1,
        );
      } else if (event.key === "ArrowRight") {
        moveToRef.current(
          activeIndexRef.current + 1,
        );
      }
    };

    window.addEventListener(
      "popstate",
      handlePopState,
    );
    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.documentElement.style
        .overflow = previousOverflow;
      delete document.documentElement
        .dataset.imageLightboxOpen;
      if (wheelTimerRef.current) {
        window.clearTimeout(
          wheelTimerRef.current,
        );
      }
      wheelTimerRef.current = null;
      wheelLockedRef.current = false;
      wheelDeltaRef.current = 0;
      window.removeEventListener(
        "popstate",
        handlePopState,
      );
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [open, requestClose]);

  if (images.length === 0) {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={`${images[activeIndex]?.alt ?? "프로젝트 이미지"} 확대 이미지`}
      initial={
        reduceMotion
          ? false
          : { opacity: 0 }
      }
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: reduceMotion
          ? 0.01
          : 0.22,
      }}
      onPointerDown={(event) => {
        event.stopPropagation();

        const target =
          event.target instanceof Element
            ? event.target
            : null;

        if (
          target?.closest(
            `.${styles.lightboxHeader}`,
          ) ||
          target?.closest(
            `.${styles.lightboxAsset}`,
          )
        ) {
          return;
        }

        requestClose();
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
      }}
      onPointerUp={(event) => {
        event.stopPropagation();
      }}
      onClick={(event) => {
        event.stopPropagation();
      }}
      onWheel={(event) => {
        event.preventDefault();
        event.stopPropagation();

        if (
          images.length <= 1 ||
          window.performance.now() <
            interactionReadyAtRef.current ||
          wheelLockedRef.current
        ) {
          return;
        }

        const delta =
          Math.abs(event.deltaX) >
          Math.abs(event.deltaY)
            ? event.deltaX
            : event.deltaY;

        if (delta === 0) {
          return;
        }

        if (
          wheelDeltaRef.current !== 0 &&
          Math.sign(wheelDeltaRef.current) !==
            Math.sign(delta)
        ) {
          wheelDeltaRef.current = 0;
        }

        wheelDeltaRef.current += delta;

        if (
          Math.abs(wheelDeltaRef.current) <
          48
        ) {
          return;
        }

        const direction =
          wheelDeltaRef.current > 0
            ? 1
            : -1;
        wheelDeltaRef.current = 0;

        const nextIndex =
          activeIndexRef.current +
          direction;

        if (
          nextIndex < 0 ||
          nextIndex >= images.length
        ) {
          return;
        }

        wheelLockedRef.current = true;
        moveTo(nextIndex);
        wheelTimerRef.current =
          window.setTimeout(() => {
            wheelLockedRef.current = false;
            wheelTimerRef.current = null;
          }, 720);
      }}
    >
      <header
        className={styles.lightboxHeader}
      >
        <div
          className={
            styles.lightboxControls
          }
        >
          <button
            type="button"
            aria-label="이전 이미지"
            disabled={activeIndex === 0}
            onClick={() => {
              moveTo(activeIndex - 1);
            }}
          >
            ←
          </button>

          <strong>
            {String(
              activeIndex + 1,
            ).padStart(2, "0")}
            {" / "}
            {String(
              images.length,
            ).padStart(2, "0")}
          </strong>

          <button
            type="button"
            aria-label="다음 이미지"
            disabled={
              activeIndex ===
              images.length - 1
            }
            onClick={() => {
              moveTo(activeIndex + 1);
            }}
          >
            →
          </button>
        </div>

        <button
          type="button"
          className={`${styles.lightboxClose} detail-close-button`}
          aria-label="확대 이미지 닫기"
          autoFocus
          onClick={requestClose}
        >
          <span aria-hidden="true">
            ×
          </span>
        </button>
      </header>

      <div
        ref={viewportRef}
        className={styles.lightboxViewport}
      >
        <motion.div
          ref={trackRef}
          className={styles.lightboxTrack}
          drag={
            images.length > 1
              ? "x"
              : false
          }
          dragConstraints={{
            left:
              -(images.length - 1) *
              slideStep,
            right: 0,
          }}
          dragElastic={0.08}
          dragMomentum={false}
          style={{ x: trackX }}
          onPointerDown={(event) => {
            event.stopPropagation();
            animationRef.current?.stop();
          }}
          onDragEnd={(_event, info) => {
            const width = widthRef.current;
            if (width <= 0) return;

            const swipeDistanceThreshold =
              Math.min(
                Math.max(width * 0.1, 28),
                48,
              );
            const hasDistanceIntent =
              Math.abs(info.offset.x) >=
              swipeDistanceThreshold;
            const hasVelocityIntent =
              Math.abs(info.velocity.x) >=
              260;

            if (
              hasDistanceIntent ||
              hasVelocityIntent
            ) {
              const direction =
                hasDistanceIntent
                  ? info.offset.x
                  : info.velocity.x;

              moveTo(
                activeIndexRef.current +
                  (direction < 0 ? 1 : -1),
              );
              return;
            }

            moveTo(activeIndexRef.current);
          }}
        >
          {images.map((image, index) => (
            <div
              key={`${image.src}-${index}`}
              className={
                styles.lightboxSlide
              }
              style={{
                width: viewportWidth || "100%",
              }}
            >
              <img
                className={
                  styles.lightboxAsset
                }
                src={image.src}
                alt={image.alt}
                draggable={false}
              />
            </div>
          ))}
        </motion.div>
      </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
